import './DraftView.css';
import { getFinalPointsColor } from '../../utils/playerHelpers';
import TeamBox from '../TeamBox/TeamBox';
import DraftActions from '../DraftActions/DraftActions';
import RecommendedPlayer from '../RecommendedPlayer/RecommendedPlayer';
import QuickSearch from '../QuickSearch/QuickSearch';
import Position from '../Position/Position';
import Players from '../Players/Players';

const DraftView = ({
  config,
  draftedPlayers,
  popup,
  setPopup,
  autocomplete,
  autocompleteRef,
  editingTeamName,
  setEditingTeamName,
  quickSearch,
  quickSearchResults,
  recommendedPlayer,
  recommendedEV,
  isRecommending,
  isRecommendationExpanded,
  setIsRecommendationExpanded,
  isFullAutoDrafting,
  positionEVs,
  futureProjected,
  showPlayersView,
  setShowPlayersView,
  draftHistory,
  redoStack,
  availablePlayers,
  selectedPlayers,
  playersByPosition,
  // Functions
  handleQuickSearch,
  handleQuickDraft,
  handlePositionClick,
  handlePlayerSelect,
  handleUndo,
  handleRedo,
  handleAutoDraft,
  handleFullAutoDraft,
  handleSaveDraft,
  handleViewChange,
  handleTeamNameEdit,
  handleFieldInput,
  handleAutoCompleteSelect,
  handleRemovePlayer,
  getRemainingCounts,
  calculateTeamProjected,
  calculateTeamFinal,
  getPlayerFinalPoints,
  isTeamsTurn,
  getCurrentTeam,
  getPicksUntilNextTurn,
  getPositionDisplay,
  getBestAvailableForPosition,
  getFormattedTeamAndPosition,
  getPlayerProjected,
  getAllPlayers,
  isActiveDraft,
  isAutoSaving,
  currentDraftName
}) => {
  const renderPositionFields = (teamIndex) => {
    const fields = [];
    Object.entries(config.positions).forEach(([position, count]) => {
      if (count > 0) {
        for (let i = 0; i < count; i++) {
          const fieldId = `${teamIndex}-${position}-${i}`;
          const selectedPlayer = draftedPlayers[fieldId];
          const finalScore = selectedPlayer ? getPlayerFinalPoints(selectedPlayer) : null;
          fields.push(
            <div className="draft-field" key={`${position}-${i}`}>
              <label
                className="position-label"
                onClick={() => handlePositionClick(position, teamIndex, i)}
              >
                {position}:
              </label>
              <div className="input-wrapper">
                <input
                  type="text"
                  value={selectedPlayer ?
                    `${selectedPlayer} ${getFormattedTeamAndPosition(selectedPlayer, position)}` :
                    (autocomplete.fieldId === fieldId ? autocomplete.searchText : '')
                  }
                  placeholder={`${position} ${i + 1}`}
                  onChange={(e) => handleFieldInput(fieldId, position, e.target.value)}
                  onClick={() => !selectedPlayer && handleFieldInput(fieldId, position, '')}
                />
                {selectedPlayer && (
                  <>
                    <button
                      className="remove-player"
                      onClick={() => handleRemovePlayer(fieldId, selectedPlayer)}
                    >
                      ×
                    </button>
                    <span className="player-projected-points">
                      {getPlayerProjected(selectedPlayer)}
                      {finalScore !== null && (
                        <strong
                          className="player-final-points"
                          style={{ color: getFinalPointsColor(finalScore, parseFloat(getPlayerProjected(selectedPlayer))) }}
                        > ({finalScore.toFixed(1)})</strong>
                      )}
                    </span>
                  </>
                )}
                {autocomplete.isOpen && autocomplete.fieldId === fieldId && autocomplete.suggestions.length > 0 && (
                  <div className="autocomplete-dropdown" ref={autocompleteRef}>
                    {autocomplete.suggestions.map((player, index) => (
                      <div
                        key={index}
                        className="autocomplete-item"
                        onClick={() => handleAutoCompleteSelect(player)}
                      >
                        <span className="player-name">{player.name}</span>
                        <span className="player-team">{player.team}</span>
                        <span className="player-projected">{player.projected.toFixed(1)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        }
      }
    });
    return fields;
  };

  const renderTeamBoxes = () => {
    const boxes = [];
    for (let i = 0; i < config.teamCount; i++) {
      boxes.push(
        <TeamBox
          key={`team-${i}`}
          teamIndex={i}
          config={config}
          draftedPlayers={draftedPlayers}
          editingTeamName={editingTeamName}
          isActive={isTeamsTurn(i)}
          teamProjected={calculateTeamProjected(i)}
          teamFinal={calculateTeamFinal(i)}
          onTeamNameEdit={handleTeamNameEdit}
          onEditStart={setEditingTeamName}
          onEditEnd={() => setEditingTeamName(null)}
          renderPositionFields={renderPositionFields}
        />
      );
    }
    return boxes;
  };

  return (
    <div className="draft-view">
      <div className="draft-top-fixed">
        <div className="draft-info-boxes">
          <div className="remaining-players-box">
            <div className="remaining-counts">
              {Object.entries(getRemainingCounts()).map(([pos, count]) => (
                <div key={pos} className={`position-count ${pos === 'TOTAL' ? 'total-count' : ''}`}>
                  <span>{pos}:</span>
                  <strong>{count}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="quick-search-section">
          <QuickSearch
            quickSearch={quickSearch}
            onQuickSearchChange={handleQuickSearch}
            quickSearchResults={quickSearchResults}
            onQuickDraft={handleQuickDraft}
            getPositionDisplay={getPositionDisplay}
          />
          <RecommendedPlayer
            player={recommendedPlayer}
            ev={recommendedEV}
            isLoading={isRecommending}
            isExpanded={isRecommendationExpanded}
            currentTeam={getCurrentTeam()}
            picksUntilNext={getPicksUntilNextTurn()}
            futureProjected={futureProjected}
            positionEVs={positionEVs}
            onToggleExpand={() => setIsRecommendationExpanded(!isRecommendationExpanded)}
            onQuickDraft={handleQuickDraft}
            getPositionDisplay={getPositionDisplay}
            getBestAvailableForPosition={getBestAvailableForPosition}
          />
        </div>
      </div>
      <div className="teams-container">
        {renderTeamBoxes()}
      </div>
      <div className="form-actions">
        <button
          className="cancel-button"
          onClick={() => {
            if (isActiveDraft()) {
              handleViewChange('initial');
            } else {
              handleViewChange('config');
            }
          }}
        >
          Back
        </button>
        <button
          className="save-button"
          onClick={handleSaveDraft}
        >
          {isAutoSaving ? 'Saving...' : (currentDraftName ? `Save ${currentDraftName}` : 'Save Draft')}
        </button>
      </div>

      {popup.isOpen && (
        <Position
          position={popup.position}
          onClose={() => setPopup({ ...popup, isOpen: false })}
          onSelect={handlePlayerSelect}
          availablePlayers={availablePlayers}
          selectedPlayers={selectedPlayers}
          draftedPlayers={draftedPlayers}
          config={config}
        />
      )}

      <DraftActions
        canUndo={draftHistory.length > 0}
        canRedo={redoStack.length > 0}
        canAutoDraft={!!recommendedPlayer && !isRecommending}
        canFullAutoDraft={isFullAutoDrafting || !!recommendedPlayer}
        isFullAutoDrafting={isFullAutoDrafting}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onAutoDraft={handleAutoDraft}
        onFullAutoDraft={handleFullAutoDraft}
        onShowPlayers={() => setShowPlayersView(true)}
      />

      {showPlayersView && (
        <Players
          onClose={() => setShowPlayersView(false)}
          players={getAllPlayers()}
        />
      )}
    </div>
  );
};

export default DraftView;
